import { getGroupLogo, removeGroupLogo, uploadGroupLogo } from '@/lib/api';
import { ApiError } from '@/lib/api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from './use-toast';
const groupLogoQueryKey = (groupId) => ['tc-group-logo', groupId];
// The GET returns a signed URL good for about an hour, so the cached copy has a
// hard shelf life the app must stay inside: serving it after the signature
// expires renders a broken image with no error the UI can catch. 20 minutes
// leaves a 3x margin — a settings modal left open across a long session
// refetches well before anything it holds can go stale on the wire — while
// still absorbing the tab-switching that makes a 0-staleTime query refetch on
// every mount.
const LOGO_STALE_TIME_MS = 20 * 60 * 1000;
/**
 * A teleradiology group's logo: read, replace, remove.
 *
 * The logo is the group's identity on the anonymous Link enrollment landing
 * page, which is the whole reason it exists — a referring practice recognizes
 * their radiologist's mark before they read a word of copy.
 *
 * Authorization is the server's: only a group admin may write, and a 403 is
 * surfaced rather than predicted, because the client has no per-group role to
 * predict it from (the TC profile carries group membership, not the caller's
 * role in the group being viewed).
 */
export function useGroupLogo(groupId) {
    const { t } = useTranslation('settings');
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const logo = useQuery({
        queryKey: groupLogoQueryKey(groupId),
        queryFn: () => getGroupLogo(groupId),
        staleTime: LOGO_STALE_TIME_MS,
    });
    const describe = (error) => {
        if (error instanceof ApiError && error.status === 403) {
            return t('settings.tcGroups.branding.errorForbidden');
        }
        if (error instanceof ApiError && error.status === 400) {
            return t('settings.tcGroups.branding.errorRejected');
        }
        return t('settings.tcGroups.branding.errorGeneric');
    };
    const upload = useMutation({
        mutationFn: (file) => uploadGroupLogo(groupId, file),
        onSuccess: (result) => {
            // The PUT already answers with the fresh signed URL, so seed the cache
            // with it rather than invalidating: the preview swaps on the same tick
            // and the just-minted signature is newer than anything a refetch returns.
            queryClient.setQueryData(groupLogoQueryKey(groupId), result);
            toast({ title: t('settings.tcGroups.branding.uploadedTitle') });
        },
        onError: (error) => toast({
            title: t('settings.tcGroups.branding.uploadFailed'),
            description: describe(error),
            variant: 'destructive',
        }),
    });
    const remove = useMutation({
        mutationFn: () => removeGroupLogo(groupId),
        onSuccess: () => {
            queryClient.setQueryData(groupLogoQueryKey(groupId), { logoUrl: null });
            toast({ title: t('settings.tcGroups.branding.removedTitle') });
        },
        onError: (error) => toast({
            title: t('settings.tcGroups.branding.removeFailed'),
            description: describe(error),
            variant: 'destructive',
        }),
    });
    return { logo, upload, remove };
}
